import Api from './applications';
import { getFileURL } from './storage/s3';
import config from '../config/config';

/**
 * Tenant profile service — backed by the dedicated profile API (GET/PUT /profile/my).
 * Each tenant has one universal profile not tied to any specific apartment.
 * Slug and profileActive are persisted to the backend PROFILE entity so the
 * shareable CV URL stays the same across all devices/browsers.
 */

const generateSlug = (): string => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    let binary = '';
    bytes.forEach(b => { binary += String.fromCharCode(b); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const getOwnProfile = async (_userId: string): Promise<any> => {
    const profile = await Api.getMyProfile();

    const slug: string = profile?.slug || '';
    const profileActive: boolean = profile?.profileActive === true;

    const data = { ...(profile || {}) };
    if (data.profilePictureKey) {
        data.profilePicture = await getFileURL(data.profilePictureKey);
    }

    return {
        userId: _userId,
        slug,
        isActive: profileActive,
        data,
        identityVerified: false,
        bankDataVerified: false,
    };
};

const saveProfile = async (_userId: string, data: any): Promise<any> => {
    // profilePicture (Blob/URL) is handled by the saga — saga uploads to S3 and sets profilePictureKey
    const { profilePicture, ...profileData } = data;
    await Api.updateMyProfile(profileData);
    return getOwnProfile(_userId);
};

const revokeSlug = async (_userId: string): Promise<{ success: boolean; isActive: boolean }> => {
    await Api.updateMyProfile({ profileActive: false });
    return { success: true, isActive: false };
};

const regenerateSlug = async (_userId: string): Promise<{ slug: string; isActive: boolean }> => {
    const slug = generateSlug();
    await Api.updateMyProfile({ slug, profileActive: true });
    return { slug, isActive: true };
};

const getPublicProfile = async (slug: string): Promise<any> => {
    const baseUrl = config.apiGateway.applications.URL;
    const response = await fetch(`${baseUrl}/profile/public/${encodeURIComponent(slug)}`);
    if (!response.ok) {
        const error: any = new Error('Profile not found');
        error.response = { status: response.status };
        throw error;
    }
    const result = await response.json();
    return {
        userId: '',
        slug,
        isActive: true,
        name: result.name || '',
        language: result.language || 'fi',
        profilePictureUrl: result.profilePictureUrl,
        data: result.data,
        identityVerified: false,
        bankDataVerified: false,
    };
};

const listPublicProfiles = async (): Promise<any[]> => {
    const baseUrl = config.apiGateway.applications.URL;
    const response = await fetch(`${baseUrl}/profiles/public`);
    if (!response.ok) {
        throw new Error('Failed to load public profiles');
    }
    return response.json();
};

export default {
    getOwnProfile,
    saveProfile,
    revokeSlug,
    regenerateSlug,
    getPublicProfile,
    listPublicProfiles
};
